import { faTimesCircle } from '@fortawesome/pro-light-svg-icons/faTimesCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { MouseEvent, ReactNode, useEffect, useRef } from 'react';
import ReactDom from 'react-dom';
import { portalRoot } from 'src/client/helpers/portalRoot';
import styled from 'styled-components/macro';

const Z_INDEX = 2000;

type Props = {
  BackdropComponent?: React.ComponentType;
  BodyComponent?: React.ComponentType;
  children?: ReactNode;
  className?: string;
  dialogClassName?: string;
  DialogComponent?: React.ComponentType;
  lockBackgroundScroll?: boolean;
  onClose: () => void;
  show: boolean;
  zIndex?: number;
};

export const AdvancedModal = (props: Props) => {
  const {
    BackdropComponent = ModalBackdrop,
    BodyComponent = ModalBody,
    className,
    dialogClassName,
    DialogComponent = ModalDialog,
    lockBackgroundScroll = false,
    onClose,
    show,
    zIndex = Z_INDEX,
  } = props;

  const modal = useRef<HTMLDivElement>(null);

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target !== modal.current) return;
    onClose();
  };

  useEffect(() => {
    if (!show || !lockBackgroundScroll) return;

    const htmlElement = document.documentElement;
    const bodyElement = document.body;
    const currentLockCount = Number(bodyElement.dataset.modalScrollLockCount || '0');

    if (currentLockCount === 0) {
      bodyElement.dataset.modalPreviousOverflow = bodyElement.style.overflow;
      htmlElement.dataset.modalPreviousOverflow = htmlElement.style.overflow;
      bodyElement.style.overflow = 'hidden';
      htmlElement.style.overflow = 'hidden';
    }

    bodyElement.dataset.modalScrollLockCount = String(currentLockCount + 1);

    return () => {
      const nextLockCount = Math.max(Number(bodyElement.dataset.modalScrollLockCount || '1') - 1, 0);

      if (nextLockCount === 0) {
        bodyElement.style.overflow = bodyElement.dataset.modalPreviousOverflow || '';
        htmlElement.style.overflow = htmlElement.dataset.modalPreviousOverflow || '';
        delete bodyElement.dataset.modalScrollLockCount;
        delete bodyElement.dataset.modalPreviousOverflow;
        delete htmlElement.dataset.modalPreviousOverflow;
        return;
      }

      bodyElement.dataset.modalScrollLockCount = String(nextLockCount);
    };
  }, [lockBackgroundScroll, show]);

  if (!show) return null;

  return ReactDom.createPortal(
    <BackdropComponent ref={modal} onClick={handleClick} style={{ zIndex }}>
      <DialogComponent className={dialogClassName} role="dialog">
        <CloseButton icon={faTimesCircle} onClick={() => onClose()} />
        <BodyComponent className={className}>
          <ScrollWrapper>{props.children}</ScrollWrapper>
        </BodyComponent>
      </DialogComponent>
    </BackdropComponent>,
    portalRoot,
  );
};

export const ModalBackdrop = styled.div`
  width: 100%;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.4);
  position: fixed;
  transition: 0.3s opacity ease;
  top: 0px;
  display: flex;
  flex-direction: column;
  place-content: center center;
`;

export const ModalDialog = styled.div`
  background-color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 6px;
  box-shadow: 0 3px 9px rgb(0 0 0 / 50%);
  outline: 0;
  width: auto;
  margin: 0 auto;
  max-width: 80%;
  max-height: 70vh;
  position: relative;
`;

export const ModalBody = styled.div`
  margin: 0;
  padding: 35px;
  overflow: hidden;
  box-sizing: border-box;
  height: 100%;
`;

export const CloseButton = styled(FontAwesomeIcon)`
  cursor: pointer;
  font-size: 30px;
  display: flex;
  place-content: center center;
  position: absolute;
  top: 0;
  right: 0;
  background: black;
  color: white;
  border-radius: 50%;
  box-shadow: -3px 3px 13px 0px rgba(0, 0, 0, 0.4);
  transform: translate(50%, -50%);
  z-index: 10;
`;

const ScrollWrapper = styled.div`
  overflow-x: hidden;
  overflow-y: auto;
  max-height: 100%;
`;
